import * as THREE from "three";
import CameraControls from "camera-controls";
import { viewerColors } from "@/utils/viewer-colors";
import { RENDERING_ORDER } from "@/constants";
import { markAsAnnotation } from "@/utils/annotation-depth";

export const enableControls = function () {
  this.cameraControls.enabled = true;
};

export const cleanNavigationSetup = function () {
  this.navigationMenu = null;
  this.disableDefaultNavigation();
};

export const restoreDefaultNavigation = function () {
  this.cameraControls.mouseButtons.left = CameraControls.ACTION.ROTATE;
};

export const disableDefaultNavigation = function () {
  this.cameraControls.mouseButtons.left = CameraControls.ACTION.NONE;
};

export const openHelpModal = function () {
  this.openHelperModal = true;
};

export const closeHelperModal = function () {
  this.openHelperModal = false;
};

export const toggleNavigation = function (value) {
  // Check what mode is currently active and disable its functions
  if (this.active !== undefined) {
    // Find the current active mode and disable its functions
    const currentMode = this.modes.find((mode) => mode.value === this.active);
    if (currentMode && currentMode.disableFunction) {
      this.previousMode = this.active;
      if (this.previousMode === 2) {
        currentMode.disableFunction("fromPanOrbiting");
      } else {
        currentMode.disableFunction(true);
      }
    }
  }

  if (value === 0) {
    this.cameraControls.mouseButtons.left = CameraControls.ACTION.ROTATE;
  } else if (value === 1) {
    this.cameraControls.mouseButtons.left = CameraControls.ACTION.TRUCK;
    this.removeZoomCenterDot();
  }
  this.navigationMenu = value;
};

export const resetCameraPosition = function () {
  this.showGreenFullScreen = true;
  
  const modelHeight = this.calculateModelHeight() * 2;
  const modelCenter = this.modelObject.position;
  
  if(this.viewPlane === "XY") {
    this.camera.position.set(0, modelHeight, 0);
    this.cameraControls.setLookAt(0, modelHeight, 0, modelCenter.x, modelCenter.y, modelCenter.z, true);
  } else if (this.viewPlane === "XZ") {
    this.camera.position.set(0, 0, modelHeight);
    this.cameraControls.setLookAt(0, 0, modelHeight, modelCenter.x, modelCenter.y, modelCenter.z, true);
  } else if (this.viewPlane === "YZ") {
    this.camera.position.set(modelHeight, 0, 0);
    this.cameraControls.setLookAt(modelHeight, 0, 0, modelCenter.x, modelCenter.y, modelCenter.z, true);
  }
  
  this.removeZoomCenterDot();
  setTimeout(() => {
    this.showGreenFullScreen = false;
  }, 1000);
};

export const recenterCameraOnArea = function (area) {
  if (!area || !area.points || area.points.length === 0) return;

  const vertices = area.points
    .map((p) => p.position)
    .filter((p) => p);
  if (vertices.length === 0) return;

  const centroid = vertices
    .reduce((acc, v) => acc.add(v), new THREE.Vector3())
    .divideScalar(vertices.length);

  const rawNormal = area.normal ?? area.trianglePlane?.normal;
  if (!rawNormal) return;
  const normal = new THREE.Vector3(rawNormal.x, rawNormal.y, rawNormal.z);
  if (normal.lengthSq() === 0) return;
  normal.normalize().negate();

  const boundingRadius = vertices.reduce(
    (max, v) => Math.max(max, v.distanceTo(centroid)),
    0
  );

  // Use perspectiveCamera.fov so distance is stable regardless of which camera
  // is currently active (orthographic mode swaps this.camera but keeps perspectiveCamera around).
  const fovDeg = this.perspectiveCamera?.fov ?? this.camera?.fov ?? 50;
  const fovRad = THREE.MathUtils.degToRad(fovDeg);
  const distance = (boundingRadius / Math.sin(fovRad / 2)) * 1.4;

  const camPos = centroid
    .clone()
    .add(normal.multiplyScalar(distance));

  this.cameraControls.setLookAt(
    camPos.x,
    camPos.y,
    camPos.z,
    centroid.x,
    centroid.y,
    centroid.z,
    true
  );

  this.removeZoomCenterDot();
};

export const addZoomCenter = function (event) {
  this.suppressClickDefault(event);

  if (this.isCtrlDown || this.isAltDown || this.isShiftDown) return;

  if (event.target.tagName !== "CANVAS") return;

  let canvasBounds = this.renderer.getContext().canvas.getBoundingClientRect();
  this.mouse.x =
    ((event.clientX - canvasBounds.left) /
      (canvasBounds.right - canvasBounds.left)) *
      2 -
    1;
  this.mouse.y =
    -(
      (event.clientY - canvasBounds.top) /
      (canvasBounds.bottom - canvasBounds.top)
    ) *
      2 +
    1;

  this.mouse.x += 0.01;
  this.mouse.y -= 0.013;

  this.raycaster.setFromCamera(this.mouse, this.camera);

  // disable marker adding/connecting when drag is on
  if (this.dragOn) return;

  let intersects = this.raycaster.intersectObject(this.modelObject.children[0]);

  if (intersects.length < 1) return;

  let o = intersects[0];

  let pIntersect = o.point.clone();
  this.scene.worldToLocal(pIntersect);

  const dotGeometry = new THREE.SphereGeometry(0.1, 64, 32);
  const dotMaterial = new THREE.MeshBasicMaterial({
    color: viewerColors.ZOOM_POINT_COLOR,
  });
  const dot = new THREE.Mesh(dotGeometry, dotMaterial);
  dot.position.x = pIntersect.x;
  dot.position.y = pIntersect.y;
  dot.position.z = pIntersect.z;

  this.cameraControls.moveTo(
    dot.position.x,
    dot.position.y,
    dot.position.z,
    true
  );

  dot.mousePosition = {
    x: this.mouse.x,
    y: this.mouse.y,
  };

  // The zoom pip is cursor feedback rather than part of the scene: it stays on
  // top of every other annotation, so it neither tests nor writes depth.
  markAsAnnotation(dot, {
    renderOrder: RENDERING_ORDER.ZOOM_CENTER,
    occludes: false,
  });

  this.removeZoomCenterDot();
  this.scene.add(dot);
  this.zoomCenterDot = dot;
};

export const removeZoomCenterEventListener = function () {
  window.removeEventListener("dblclick", this.addZoomCenter, false);
  this.removeZoomCenterDot();
};

export const removeZoomCenterDot = function () {
  if (this.zoomCenterDot) {
    const object = this.scene.getObjectById(this.zoomCenterDot.id);
    this.scene.remove(object);
  }
};

export const keyDownInit = function (event) {
  if (event.metaKey) {
    this.isShiftDown = false;
    this.isAltDown = false;
    return;
  }

  if (event.shiftKey && !this.isShiftDown) {
    this.isShiftDown = true;
    this.cameraControls.mouseButtons.left = CameraControls.ACTION.TRUCK;
    this.cameraControls.mouseButtons.middle = CameraControls.ACTION.TRUCK;
    this.cameraControls.mouseButtons.right = CameraControls.ACTION.TRUCK;
    this.removeZoomCenterDot();
  } else if (event.altKey && !this.isAltDown) {
    this.isAltDown = true;
    this.cameraControls.mouseButtons.left = CameraControls.ACTION.ROTATE;
  }
};

export const keyUpInit = function (event) {
  const target = event.target;
  const isTypingInField =
    target &&
    (target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.isContentEditable);

  const shortcutsBlocked = isTypingInField || this.keyboardShortcutsSuspended;

  if (event.code === "Space") {
    if (shortcutsBlocked) return;
    this.resetCameraPosition();
  }

  const isEditingName = this.$refs.mainSidebaRef?.isEditingName?.();
  if(!shortcutsBlocked && this.currentUser.isEmployee && !this.selectedArea && !isEditingName) {
    if (event.key === "3") {
      this.viewPlane = "YZ";
      this.resetCameraPosition();
    } else if (event.key === "1") {
      this.viewPlane = "XZ";
      this.resetCameraPosition();
    } else if (event.key === "7") {
      this.viewPlane = "XY";
      this.resetCameraPosition();
    }
  }

  if (!event.shiftKey && this.isShiftDown) {
    // if only shift key is released but the left mouse is stil down, a new click event is fired
    // this snippet is to prevent that extra event from causing a new point to be added
    // this is only relevant for measurement modes because that's when we have an preliminary line
    if (this.active === 4) {
      document.removeEventListener("click", this.addMeasurementPoint, false);
      setTimeout(() => {
        document.addEventListener("click", this.addMeasurementPoint, false);
      }, 500);
    } else if (this.active === 1) {
      document.removeEventListener("click", this.addMarker, false);

      setTimeout(() => {
        document.addEventListener("click", this.addMarker, false);
      }, 500);
    }

    this.isShiftDown = false;
    if (this.navigationMenu === 0) {
      this.cameraControls.mouseButtons.left = CameraControls.ACTION.ROTATE;
    } else if (this.navigationMenu === 1) {
      this.cameraControls.mouseButtons.left = CameraControls.ACTION.TRUCK;
    } else {
      this.cameraControls.mouseButtons.left = this.currentLeftClickAction;
      this.cameraControls.mouseButtons.middle = CameraControls.ACTION.ROTATE;
      this.cameraControls.mouseButtons.right = CameraControls.ACTION.ROTATE;
    }
  } else if (!event.altKey && this.isAltDown) {
    // if only alt/options key is released but the left mouse is stil down, a new click event is fired
    // this snippet is to prevent that extra event from causing a new point to be added
    // this is only relevany for measurement modes because that's when we have an preliminary line
    if (this.active === 4) {
      document.removeEventListener("click", this.addMeasurementPoint, false);

      setTimeout(() => {
        document.addEventListener("click", this.addMeasurementPoint, false);
      }, 500);
    } else if (this.active === 1) {
      document.removeEventListener("click", this.addMarker, false);

      setTimeout(() => {
        document.addEventListener("click", this.addMarker, false);
      }, 500);
    }
    this.isAltDown = false;
    if (this.navigationMenu === 0) {
      this.cameraControls.mouseButtons.left = CameraControls.ACTION.ROTATE;
    } else if (this.navigationMenu === 1) {
      this.cameraControls.mouseButtons.left = CameraControls.ACTION.TRUCK;
    } else {
      this.cameraControls.mouseButtons.left = this.currentLeftClickAction;
    }
  }
};

export const keyDownInitWindow = function (e) {
  if (e.code === "Space" && e.target === document.body) {
    e.preventDefault();
  }
};

export const resetCommandKeyStates = function () {
  this.isAltDown = false;
  this.isShiftDown = false;
};
